import React from 'react';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';

export default function CartDrawer() {
  const { state, closeCart, removeItem, updateQuantity, subtotal, getItemKey } = useCart();
  const navigate = useNavigate();

  if (!state.isOpen) return null;

  const shipping = subtotal >= 10000 ? 0 : 250;
  const total = subtotal + shipping;

  const formatPrice = (price: number) => `Rs.${price.toLocaleString()}`;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={closeCart} />
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 flex flex-col shadow-2xl" style={{ animation: 'slideInRight 0.3s ease-out' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-serif font-semibold text-gray-900">Your Cart ({state.items.reduce((s,i) => s+i.quantity, 0)})</h2>
          <button onClick={closeCart} aria-label="Close cart" title="Close cart" className="p-2 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Free shipping progress */}
        {subtotal > 0 && subtotal < 10000 && (
          <div className="px-6 py-2.5 bg-amber-50 border-b border-amber-100">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-amber-700 font-medium">Add {formatPrice(10000 - subtotal)} more for FREE delivery</span>
              <span className="text-amber-600 font-semibold">{Math.round((subtotal / 10000) * 100)}%</span>
            </div>
            <div className="h-1.5 bg-amber-200 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${Math.min((subtotal / 10000) * 100, 100)}%` } as React.CSSProperties} />
            </div>
          </div>
        )}
        {subtotal >= 10000 && (
          <div className="px-6 py-2.5 bg-green-50 border-b border-green-100 flex items-center gap-2">
            <span className="text-green-600 text-sm">🎉</span>
            <span className="text-green-700 text-xs font-semibold">You've unlocked FREE delivery!</span>
          </div>
        )}

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {state.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag size={64} className="text-gray-200 mb-4" />
              <p className="text-gray-500 font-medium">Your cart is empty</p>
              <p className="text-gray-400 text-sm mt-1">Add some products to get started</p>
              <button onClick={closeCart} className="mt-6 bg-gray-900 text-white px-8 py-3 text-sm font-medium uppercase tracking-wide hover:bg-gray-700 transition-colors">
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {state.items.map(item => {
                const key = getItemKey(item);
                return (
                  <div key={key} className="flex gap-4 py-4 border-b border-gray-50">
                    <div className="w-20 h-24 bg-gray-50 flex-shrink-0 overflow-hidden">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 leading-tight truncate">{item.name}</h3>
                      {(item.size || item.color) && (
                        <p className="text-xs text-gray-500 mt-1">
                          {item.size && `Size: ${item.size}`}
                          {item.size && item.color && ' / '}
                          {item.color && `Color: ${item.color}`}
                        </p>
                      )}
                      <p className="text-sm font-semibold text-gray-900 mt-1">{formatPrice(item.price)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          aria-label="Decrease quantity"
                          onClick={() => updateQuantity(key, item.quantity - 1)}
                          className="w-7 h-7 border border-gray-300 flex items-center justify-center hover:border-gray-900 transition-colors"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="text-sm w-8 text-center font-medium">{item.quantity}</span>
                        <button
                          aria-label="Increase quantity"
                          onClick={() => updateQuantity(key, item.quantity + 1)}
                          className="w-7 h-7 border border-gray-300 flex items-center justify-center hover:border-gray-900 transition-colors"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button aria-label="Remove item" title="Remove" onClick={() => removeItem(key)} className="text-gray-400 hover:text-red-500 transition-colors">
                        <X size={16} />
                      </button>
                      <span className="text-sm font-semibold">{formatPrice(item.price * item.quantity)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {state.items.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal</span><span>{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Shipping</span>
                <span className={shipping === 0 ? 'text-green-600 font-medium' : ''}>{shipping === 0 ? 'FREE' : formatPrice(shipping)}</span>
              </div>
              {shipping > 0 && <p className="text-xs text-gray-400">Add {formatPrice(10000 - subtotal)} more for free delivery</p>}
              <div className="flex justify-between text-base font-semibold text-gray-900 border-t border-gray-200 pt-2">
                <span>Total</span><span>{formatPrice(total)}</span>
              </div>
            </div>
            <button
              onClick={() => { closeCart(); navigate('/checkout'); }}
              className="w-full bg-gray-900 text-white py-3.5 text-sm font-semibold uppercase tracking-widest hover:bg-gray-700 transition-colors"
            >
              Proceed to Checkout
            </button>
            <button onClick={closeCart} className="w-full mt-2 border border-gray-300 py-3 text-sm font-medium text-gray-700 hover:border-gray-900 transition-colors">
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
}
